import { createTheme, responsiveFontSizes } from '@mui/material/styles';

import type { IMuiTheme } from './MuiTheme';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const primaryTheme: IMuiTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: '#06AED4',
      contrastText: '#FFF',
      light: '#A5F0FC',
      dark: '#088AB2',
      50: '#ECFDFF',
      100: '#CFF9FE',
      200: '#A5F0FC',
      300: '#67E3F9',
      400: '#22CCEE',
      500: '#06AED4',
      600: '#088AB2',
      700: '#0E7090',
      800: '#155B75',
      900: '#164C63',
    },
    secondary: {
      main: '#11111F',
      contrastText: '#FFFFFF',
      100: '#101828',
      200: '#1F2937',
      300: '#D0D5DD',
      400: '#E5E7EB',
    },

    warning: {
      main: '#FEF0C7',
      contrastText: '#F79009',
      dark: '#FEDF89',
      100: '#FEF0C7',
      200: '#FEDF89',
      500: '#F79009',
    },
    error: {
      main: '#F04438',
      contrastText: '#fff',
      100: '#FEE4E2',
      600: '#D92D20',
      500: '#F04438',
    },
    info: {
      main: '#E7E7E7',
      contrastText: '#13121299',
    },
    font: {
      main: '#344054',
      contrastText: '#fff',
      100: '#000',
      900: '#475467',
    },
    selected: {
      main: '#F9F5FF',
      contrastText: '#088AB2',
      dark: '#CFF9FE',
      600: '#088AB2',
    },
    tertiary1: {
      main: '#fff',
      contrastText: '#475467',
      dark: '#F9FAFB',
    },
    tertiary2: {
      main: '#fff',
      contrastText: '#088AB2',
      dark: '#F9F5FF',
    },
    buttonGray: {
      main: '#fff',
      contrastText: '#344054',
      dark: '#F9FAFB',
    },
  },
  typography: {
    fontFamily: `"Inter", sans-serif -webkit-font-smoothing: antialiased -moz-osx-font-smoothing: grayscale !important`,
  },
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          background: 'white', // set the desired height,
          borderWidth: '1px',
          borderRadius: '8px',
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        color: 'secondary',
        size: 'medium',
      },
      styleOverrides: {
        root: {
          borderWidth: '1px',
          borderRadius: '8px',
          '& input,& textarea': {
            padding: '10px 14px',
            background: 'white',
            '&::placeholder': {
              color: '#687287',
              opacity: 'initial',
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.font.main,
          fontWeight: '500',
          fontSize: '14px',
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-input': {
            padding: '10px 14px',
            borderRadius: '8px',
            borderWidth: '1px',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        avatar: {
          marginBottom: 'auto',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          // minHeight: "70px !important",
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
        },
        indicator: {
          backgroundColor: 'black',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          transform: 'none',
          color: theme.palette.font.main,
          fontWeight: '600',
          fontSize: '14px',
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'inherit',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 600,
          padding: '7px 16px',
          letterSpacing: '0.5px',
          ':disabled': {
            background: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
          },

          // '&:hover': {
          //   background: '#088AB2',
          //   // border: '1px solid #CCCCCC',
          // },
        }),
      },
      variants: [
        {
          props: {
            variant: 'contained',
            disabled: true,
          },
          style: ({ theme }) => ({ background: '#A5F0FC', color: 'red' }),
        },
      ],
      // variants: [
      //   {
      //     props: { variant: 'roundedContained' }, // color/bg not working
      //     style: {
      //       padding: '6px 16px',
      //       borderRadius: '21px',
      //       background: '#50A1EB',
      //       // fontWeight: 600,
      //       border: '1px solid #50A1EB',
      //       color: 'white',
      //       '&:hover': {
      //         background: '#248AE6',
      //       },
      //     },
      //   },
      // ],
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          // backgroundColor: "#5C5C5C",
          borderRadius: '16px',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: '0px',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1px 2px 0px',
        },
      },
    },
    MuiChip: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        colorSuccess: {
          color: '#039855 !important',
        },
        colorError: {
          color: '#B54708 !important',
        },
        colorWarning: {
          color: '#344054 !important',
        },
        colorInfo: {
          color: '#000000 !important',
        },
        outlined: {
          border: '1.5px solid ',
          borderRadius: '16px',
          fontWeight: '500',
        },
        label: {
          fontSize: '12px',
          fontWeight: '500',
          textAlign: 'center',
        },
      },
    },
  },
});

export default responsiveFontSizes(createTheme(primaryTheme));
